/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { MimeType, Platforms } from '@constants';
import { IFormat, IPlatform, IToken } from '@interface/token';
import { PREVIEW_VIDEO_FILENAME_PREFIX, PREVIEW_VIDEO_SUBTITLES_PREFIX } from '@lib/media/config';
import { formatBytes } from '@utils/format';
import { getURLFromIPFSURI, Node } from '@utils/ipfs';

export const getTags = (tags: any[]) => {
  return tags.map((e) => e.tag.name);
};

// defines a max of images on the srcset on feeds
// 1 would generate the 480px image
// 2 would generate the 480px + 960px image
const FEED_MAX_IMAGE_PREVIEW = 2; // min value is 1, recommended value is 2

export const getRandomIPFSNode = () => {
  const options = [Node.INFURA, Node.IPFS /* , Node.VERSUM */];
  const index = Math.floor(Math.random() * options.length);
  return options[index];
};

export const getWebDimensions = (formats: IFormat[], platform: IPlatform) => {
  if (platform.name === Platforms.VERSUM) {
    if (formats.length) {
      const og = formats[0]?.dimensions;
      if (og?.unit === 'ratio') {
        const [width, height] = og.value.split(':');
        const a = parseFloat(width) / parseFloat(height);
        return {
          width: a * 100000,
          height: 100000,
        };
      }
      if (og?.unit === 'px') {
        const [width, height] = og.value.split('x');
        return {
          width: parseFloat(width),
          height: parseFloat(height),
        };
      }
    }

    return null;
  }

  return { width: 960, height: 960 };
};

export const getDimensions = (formats: IFormat[]) => {
  // some previews are smaller than the biggest dimension available.
  let preview = formats.filter((e: any) => e.mimeType.includes('image'));

  // if there are no preview metada, try checking for the original asset
  if (preview.length === 0) {
    preview = formats;
  }

  const dimensions = preview
    .filter((e) => e.dimensions)
    .map((e) => {
      const [width, height] = e.dimensions.value.split('x');
      return {
        width: parseFloat(width),
        height: parseFloat(height),
      };
    })
    .sort((a: any, b: any) => b.width - a.width);

  // if there is a "dimensions array"
  if (dimensions.length > 0) {
    return dimensions;
  }

  // if no "dimensions array", return default settings
  return [{ width: 960, height: 960 }];
};

export const getSrcSet = (formats: IFormat[], inDetailView = false, limitToMax?: number) => {
  // get all images on formats (including the original) // changing to excluding the original
  const images = formats
    .filter((e) => e.dimensions && e.mimeType.includes('image')) // includes original (gifs)
    .sort((a: any, b: any) => {
      return a.dimensions.value.split('x')[0] - b.dimensions.value.split('x')[0]; // order from smallest to largest
    })
    .filter((_data, index: number) => {
      // on feed dont show all resolutions
      if (!inDetailView && index > FEED_MAX_IMAGE_PREVIEW - 1) {
        return false;
      }

      if (limitToMax && index > limitToMax) {
        return false;
      }

      // in detail view show all resolutions
      return true;
    })
    .sort((a: any, b: any) => {
      return b.dimensions.value.split('x')[0] - a.dimensions.value.split('x')[0]; // order from largest to smallest
    });

  // extract all dimensions and order them by largest to smaller
  const dimensions = images.map((e) => e.dimensions.value.split('x')[0]).sort((a: any, b: any) => b - a);

  // sorting order needs to be smaller to largest
  const node = inDetailView ? Node.INFURA : undefined;
  return images
    .map((e, i) => {
      const src = getURLFromIPFSURI(e.uri, node);
      return `${src} ${dimensions[i]}w`;
    })
    .join(', ');
};

// was INFURA
export const getArtifactUri = (token: IToken, nodeType?: Node) => {
  const src = getURLFromIPFSURI(token.artifact_uri, nodeType);
  const alt = token.title;
  const dimensions = getDimensions(token.formats);
  const { width, height } = dimensions[0];

  return {
    src,
    width,
    height,
    alt,
  };
};

export const getResponsiveImages = (token: IToken, inDetailView = false, limitToMax?: number) => {
  const dimensions = getDimensions(token.formats)
    .sort(({ width: a }, { width: b }) => a - b) // order from smallest to largest
    .filter((_data, index: number) => {
      // on feed view dont show the highest resolution images as they slow down page loading
      if (!inDetailView && index > FEED_MAX_IMAGE_PREVIEW - 1) {
        return false;
      }

      if (limitToMax && index > limitToMax) {
        return false;
      }

      // in detail view show all resolutions
      return true;
    });

  const { width } = dimensions[dimensions.length - 1];

  // we define the image max width to be the largest of the dimensions array width
  const sizes = `(max-width: ${width}px) 100vw, ${width}px`;
  const srcset = getSrcSet(token.formats, inDetailView, limitToMax);

  if (sizes && srcset) {
    return {
      sizes,
      srcset,
    };
  }

  return null;
};

export const getResponsiveVideos = (token: IToken, nodeType?: Node) => {
  // get all preview videos
  const previews = token.formats
    .filter((e) => e.fileName?.includes(PREVIEW_VIDEO_FILENAME_PREFIX) && e.mimeType === MimeType.MP4)
    .sort((a: any, b: any) => b - a);

  if (previews.length > 0) {
    // return both versions, its up for the WrapperComponent (JSX) to decide which one to render
    return previews.map((e) => getURLFromIPFSURI(e.uri, nodeType));
  }

  return null;
};

export const getResponsiveCover = (token: IToken) => {
  // similar to responsive images, its used on any asset that renders an image but its artifactUri is not an image.
  // e.g.: audio/3d/pdf
  // since the artifact_uri on an audio track is an mp3 and the cover is on the formats
  // we need to handle the logic differently than we do with images (which is a combination of getArtifactUri and getResponsiveImages)
  const dimensions = getDimensions(token.formats);
  const { width } = dimensions[0];

  const previews = token.formats
    .filter((e) => e.dimensions && e.mimeType.includes('image'))
    .sort((a: any, b: any) => b - a)
    .map((e) => getURLFromIPFSURI(e.uri));

  const src = previews[0];
  const sizes = `(max-width: ${width}px) 100vw, ${width}px`;
  const srcset = getSrcSet(token.formats);

  if (src && sizes && srcset) {
    return {
      src,
      sizes,
      srcset,
    };
  }

  return null;
};

export const getVideoPoster = (token: IToken) => {
  const previews = token.formats
    .filter((e) => e.dimensions && e.mimeType.includes('image'))
    .sort((a: any, b: any) => b - a)
    .map((e) => getURLFromIPFSURI(e.uri));

  return previews.length > 0 ? previews[0] : '';
};

export const getVideoSubtitles = (token: IToken) => {
  // get all subtitlestou a comer das-m
  const subtitles = token.formats
    .filter((e) => e.fileName?.includes(PREVIEW_VIDEO_SUBTITLES_PREFIX) && e.mimeType === MimeType.VTT)
    .map((st) => {
      // this function gets the subtitles fileName and generates a label and srcLang
      // because those are not TZIP21 spec
      const getPropsFromName = (value: string) => {
        let label = 'English';
        let srcLang = 'en';

        const tail = value.indexOf('.vtt');
        const key = String(value).slice(PREVIEW_VIDEO_SUBTITLES_PREFIX.length, tail);

        switch (key) {
          case 'de':
            label = 'German';
            srcLang = key;
            break;
          default:
            label = 'English';
            srcLang = 'en';
        }
        return { label, srcLang };
      };

      const { label, srcLang } = getPropsFromName(st.fileName);

      const url = getURLFromIPFSURI(st.uri, Node.CLOUDFLARE);

      return {
        label,
        srcLang,
        file: url,
      };
    });

  if (subtitles) {
    return subtitles;
  }

  return undefined;
};

export const getSmallestImageBoardPreview = (token: IToken, node?: Node) => {
  const previews = token.formats
    .filter((e) => e.dimensions && e.mimeType.includes('image'))
    .sort((a: any, b: any) => b.fileSize - a.fileSize)
    .map((e) => getURLFromIPFSURI(e.uri, node));

  const src = previews[previews.length - 1];

  if (src) {
    return {
      src,
    };
  }

  return null;
};

// returns the amount of editions the user has in their collection
export const getViewerEditionsOutsideMarket = (viewer: string, holders: any[]) => {
  return holders.reduce((a: any, c: any) => {
    if (c.holder.user[0].user.id === viewer) {
      return a + c.amount;
    }

    return a;
  }, 0);
};

// returns the amount of editions the user has on swaps
export const getViewerEditionsOnSwap = (viewer: string, swaps: []) => {
  return swaps.reduce((a: any, c: any) => {
    if (c.seller.user[0].user.id === viewer) {
      return a + c.amount_left;
    }

    return a;
  }, 0);
};

// returns the amount of editions the user has on auctions
export const getViewerEditionsOnAuction = (viewer: string, auctions: []) => {
  return auctions.reduce((a: any, c: any) => {
    if (c.seller.user[0].user.id === viewer) {
      return a + 1;
    }

    return a;
  }, 0);
};

// returns the amount of editions the user has on offers
// export const getViewerEditionsOnOffers = (viewer: string, offers: any[]) => {
//   return offers.reduce((a: any, c: any) => {
//     if (c.buyer.user[0].user.id === viewer) {
//       return a + 1;
//     }

//     return a;
//   }, 0);
// };

export const extractMedium = (token: IToken) => {
  const original = token.formats[0];

  const extractDimensions = (dimensions: any) => {
    if (dimensions) {
      return ` | ${dimensions.value}${dimensions.unit}`;
    }
    return '';
  };

  const extractSize = (size: number) => {
    if (size) {
      return ` | ${formatBytes(size)}`;
    }
    return '';
  };

  const extractDuration = (duration: any) => {
    if (duration) {
      return `, ${duration.split(':').reduce((acc: number, time: number) => 60 * acc + +time)} seconds`; // converts HH:MM:SS: to seconds
    }
    return '';
  };

  switch (token.mime) {
    case MimeType.GIF:
      return original ? `GIF${extractDimensions(original.dimensions)}${extractSize(original.fileSize)}` : 'GIF';
    case MimeType.JPEG:
      return original ? `JPG${extractDimensions(original.dimensions)}${extractSize(original.fileSize)}` : 'JPG';
    case MimeType.PNG:
      return original ? `PNG${extractDimensions(original.dimensions)}${extractSize(original.fileSize)}` : 'PNG';
    case MimeType.SVG:
      return original ? `SVG${extractDimensions(original.dimensions)}${extractSize(original.fileSize)}` : 'SVG';
    case MimeType.MP4:
      return original
        ? `MP4${extractDimensions(original.dimensions)}${extractSize(original.fileSize)}${extractDuration(
            original.duration
          )}`
        : 'MP4';
    case MimeType.OGV:
      return original
        ? `OGG${extractDimensions(original.dimensions)}${extractSize(original.fileSize)}${extractDuration(
            original.duration
          )}`
        : 'OGG';
    case MimeType.GLB:
      return 'GLB';
    case MimeType.MP3:
      return original ? `MP3${extractSize(original.fileSize)}${extractDuration(original.duration)}` : 'MP3';
    case MimeType.WAV:
      return original
        ? `WAV${extractDimensions(original.dimensions)}${extractSize(original.fileSize)}${extractDuration(
            original.duration
          )}`
        : 'WAV';
    case MimeType.WEB:
      return original ? `WEB${extractDimensions(original.dimensions)}${extractSize(original.fileSize)}` : 'WEB';
    default:
      return 'Unknown';
  }
};
